import type {
  CourseRef,
  HoleScore,
  ScoreCard,
} from "../../@apis/dto/response/score.response";

export const HOLES_PER_COURSE = 9;
export const MAX_COURSE_COUNT = 14;

export const courseOrderOf = (holeNo: number) =>
  Math.floor((holeNo - 1) / HOLES_PER_COURSE) + 1;
export const holeInCourseOf = (holeNo: number) =>
  ((holeNo - 1) % HOLES_PER_COURSE) + 1;

const courseAt = (card: ScoreCard, order: number): CourseRef | null =>
  card.courses.find((c) => c.order === order) ?? null;

export const holeAt = (card: ScoreCard, holeNo: number): HoleScore | null =>
  card.holeList.find((h) => h.holeNo === holeNo) ?? null;

const labelOf = (course: CourseRef | null, order: number) =>
  course?.courseName ?? `${order}번째 코스`;

export const courseLabelOf = (card: ScoreCard, order: number) =>
  labelOf(courseAt(card, order), order);

// 코스 홀에 연결되지 않은 슬롯만 사용자가 par 를 넣는다
export const needsUserPar = (card: ScoreCard, holeNo: number) =>
  holeAt(card, holeNo)?.coursePar == null;

const inputHoleNos = (card: ScoreCard) =>
  card.holeList
    .filter((h) => h.holeScoreId != null)
    .map((h) => h.holeNo)
    .sort((a, b) => a - b);

/** from 뒤의 첫 입력 가능 슬롯. 없으면 마지막 입력 가능 슬롯 */
export const inputHoleNoAfter = (card: ScoreCard, from: number) => {
  const holeNos = inputHoleNos(card);
  return holeNos.find((n) => n > from) ?? holeNos.at(-1) ?? from;
};

/** 이어서 칠 슬롯: 마지막으로 점수를 넣은 슬롯 다음 */
export const resumeHoleNo = (card: ScoreCard) =>
  inputHoleNoAfter(
    card,
    card.holeList.reduce(
      (max, h) => (typeof h.score === "number" ? Math.max(max, h.holeNo) : max),
      0,
    ),
  );

/** from 에서 step 방향으로 순환하며 점수를 넣을 수 있는 다음 슬롯 */
export const nextInputHoleNo = (
  card: ScoreCard,
  from: number,
  step: 1 | -1,
): number | null => {
  const n = card.holeCount;
  for (let i = 1; i <= n; i++) {
    const holeNo = ((((from - 1 + step * i) % n) + n) % n) + 1;
    if (holeAt(card, holeNo)?.holeScoreId != null) return holeNo;
  }
  return null;
};

// 코스 자리마다 9홀씩 묶는다. 코스를 고르지 않은 자리는 course 가 null
const holesByCourse = (card: ScoreCard) =>
  Array.from(
    { length: Math.ceil(card.holeCount / HOLES_PER_COURSE) },
    (_, i) => {
      const order = i + 1;
      return {
        order,
        course: courseAt(card, order),
        holes: card.holeList.filter((h) => courseOrderOf(h.holeNo) === order),
      };
    },
  );

export type ScoreSection = {
  order: number;
  title: string;
  holeNos: number[];
  pars: (number | null)[];
  scores: (number | null)[];
};

/** 표에 그릴 코스 묶음. overrides 는 슬롯별 미저장 값 */
export const toScoreSections = (
  card: ScoreCard,
  overrides?: {
    pars?: Record<number, number>;
    scores?: Record<number, number>;
  },
): ScoreSection[] =>
  holesByCourse(card).map(({ order, course, holes }) => {
    const holeNos = Array.from(
      { length: HOLES_PER_COURSE },
      (_, i) => (order - 1) * HOLES_PER_COURSE + i + 1,
    );
    const byNo = new Map(holes.map((h) => [h.holeNo, h]));
    return {
      order,
      title: labelOf(course, order),
      holeNos,
      pars: holeNos.map((n) => overrides?.pars?.[n] ?? byNo.get(n)?.par ?? null),
      scores: holeNos.map(
        (n) => overrides?.scores?.[n] ?? byNo.get(n)?.score ?? null,
      ),
    };
  });

// 숫자계산
export const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

// 표기/스타일
export const diffClass = (d: number) =>
  d > 0 ? "text-blue-500" : d < 0 ? "text-red-500" : "text-[#888]";

export const diffClassAny = (d: number | null | undefined) =>
  d == null ? "text-[#888]" : d > 0 ? "text-blue-500" : d < 0 ? "text-red-500" : "text-[#888]";

export const formatDiff = (d: number) => (d > 0 ? `(+${d})` : `(${d})`);
